import * as Sentry from '@sentry/react';
import { Microbranding } from '../../reducers/microbranding-reducer';
import { currentEnv, isFedM } from '../env';
import { isMobileOidc } from '../oidc/oidc';
import { inferCapabilitiesFromPathname } from './capability-helpers';
import { Capability } from './capability-tags';

export type SentryPayload = Record<string, unknown> | Error;

// Only turn on debug for non production and non test runs (i.e. Jest);
const debug = currentEnv !== 'prod' && process.env.NODE_ENV !== 'test';

// https://hello.atlassian.net/wiki/spaces/OBSERVABILITY/pages/3370131692/Sentry+FedRAMP+-+Getting+Started#Sentry-DSN-in-FedRAMP
const fedMSentryDSN = 'https://1c864dd6ac671ce6296e7cc6710a21a0@sentry.atlassian-us-gov-mod.com/22';
const commercialSentryDSN =
  'https://1271fcf5ddad45a1846153219eb726bd@o55978.ingest.sentry.io/4504172801622016';

Sentry.init({
  dsn: isFedM() ? fedMSentryDSN : commercialSentryDSN,
  debug,
  environment: currentEnv,
  release: process.env.REACT_APP_VERSION || 'no-version',
  enabled: currentEnv !== 'local',
  sampleRate: 0.1,
  beforeSend(event) {
    // Remove URL queryparams, they can contain PII
    if (event.request?.url) {
      event.request.url = event.request.url.split('?')[0];
      delete event.request.query_string;
    }

    // Ensure we have at least some best-guess tags for which capability might be affected
    const inferredCapabilities = inferCapabilitiesFromPathname(window.location.pathname);
    const capabilityTags = capabilitiesToTags(inferredCapabilities);
    const existingTags = event.tags || {};
    event.tags = {
      // order matters, we don't want to override any tags that were deliberately and specifically
      // set on the event already
      ...capabilityTags,
      ...existingTags,
    };

    return event;
  },
  beforeBreadcrumb(breadcrumb) {
    // Don't send breadcrumbs that may contain PII
    if (['navigation', 'fetch', 'xhr'].includes(breadcrumb.category || '')) {
      return null;
    }

    return breadcrumb;
  },
  denyUrls: [
    /^chrome:\/\//,
    /^chrome-extension:\/\//,
    /accounts\.google\.com/, // Google OneTap
    /recaptcha\.net/, // Google reCAPTCHA entry script
    /\/recaptcha\/(releases|api2|enterprise)\//, // Google static assets scripts like bundled js pulled from recaptcha.net
  ],
  ignoreErrors: [
    /^null/,
    /^undefined/,
    /Loading chunk.\d+.failed/,
    /^[^.]+\w+ is not defined/, // Ignore injected globals
    'Load Failed',
    'NetworkError',
    'Failed to fetch',
    'SecurityError', // Incognito errors
    'Object Not Found Matching Id', // Selenium webdriver
    'Non-Error promise rejection captured with value:',
    'ResizeObserver loop limit exceeded', // From browser extensions, ResizeObserver isn't used
  ],
  integrations: [
    // extracts all non-native attributes from the error object and attaches them to the event as extra data
    Sentry.extraErrorDataIntegration(),
  ],
});

export function initClient(microbranding: Microbranding) {
  Sentry.getCurrentScope().setExtra('isEmbedded', microbranding.isEmbedded);
  Sentry.getCurrentScope().setExtra('isMobileOidc', isMobileOidc(microbranding.oidcContext));
}

export function setUserId(anonymousId: string) {
  Sentry.getCurrentScope().setUser({ id: anonymousId });
}

type AppStateInitializationContext = Partial<{
  source: 'preloaded' | 'fetched';
  fetchResult: 'failed' | 'succeeded' | 'network_error';
  statusCode: number;
  statusText: string;
  errorMessage: string;
  attempt: number;
}>;

export function setAppStateInitializationContext(
  contextValue: AppStateInitializationContext
): void {
  Sentry.setContext('appStateInitialization', contextValue);
}

export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  if (!breadcrumb.level) {
    breadcrumb.level = 'info';
  }
  Sentry.addBreadcrumb(breadcrumb);
}

export function setContext(name: string, value: Sentry.Context) {
  Sentry.setContext(name, value);
}

export function setLocale(locale: string | undefined) {
  if (locale) {
    Sentry.getCurrentScope().setTag('locale', locale);
  }
}

export function setAnonymousIdTag(anonymousId: string) {
  Sentry.getCurrentScope().setTag('anonymousId', anonymousId);
}

/**
 * SENTRY TAGS
 *
 * There are Sentry alerts configured based on these tags. Take care to update the configuration of
 * these alerts when changing tag values. Adding new tag values is safe.
 */
export type RecaptchaTag = 'no_token_score' | 'no_token_checkbox' | 'unexpected_captcha_type';
export type CobrandingTag = 'loom-identity';
export type MalformedErrorTag = 'not-an-instance-of-error';
export type ErrorKindTag =
  | 'rendering' // tag to represent errors that will crash the react app when uncaught
  | 'non-rendering'; // tag to represent errors that bubble up the console but don't crash the app, e.g. event handler errors, async errors
export type OtpTag = 'otp_submission' | 'resend_otp' | 'initiate_otp';
export type CastleTag = 'castle_request_token_error' | 'castle_config_error' | 'castle_init_error';
// This can happen if a cobranded experience is missing the required components or the fallback component is missing the required components
export type CobrandingOrchestratorTag = 'missing_required_components';
export type AppStateInitializationTag =
  | 'app_state_client_error'
  | 'app_state_server_error'
  | 'app_state_network_error'
  | 'app_state_parse_error';

/**
 * Tags allow adding searchable custom data to events.
 *
 * This is useful in general for making events searchable by some custom piece of data, and for
 * configuring filters for alerts.
 *
 * See:
 * https://docs.sentry.io/platforms/javascript/enriching-events/tags/
 * https://github.com/getsentry/support/discussions/50#discussioncomment-3263832
 */
export type SentryTags = {
  recaptcha?: RecaptchaTag;
  statsigFeatureGate?: 'statsig_feature_gate_client_init';
  coBranding?: CobrandingTag;
  malformedError?: MalformedErrorTag;
  errorKind?: ErrorKindTag;
  otp?: OtpTag;
  castle?: CastleTag;
  cobrandingOrchestrator?: CobrandingOrchestratorTag;
  appStateInitialization?: AppStateInitializationTag;
};

/**
 * For capturing any exceptions in a way where they can correlated to capabilities.
 */
export function captureTaggedException(
  ex: Error | unknown,
  options: {
    capabilities: Capability[];
    tags?: SentryTags;
    extraProperties?: Record<string, unknown>;
  }
) {
  const { capabilities, tags = {}, extraProperties = {} } = options;

  Sentry.withScope(scope => {
    const capabilityTags = capabilitiesToTags(capabilities);

    scope.setTags({
      ...tags,
      ...capabilityTags,
    });

    scope.setExtras(extraProperties);

    Sentry.captureException(ex);
  });
}

/**
 * For capturing react rendering exceptions, in a way where they can be correlated to capabilities.
 * */
export function captureTaggedReactException(
  ex: Error | unknown,
  errorInfo: React.ErrorInfo,
  options: {
    capabilities: Capability[];
    tags?: SentryTags;
    extraProperties?: Record<string, unknown>;
  }
) {
  const { capabilities, tags = {}, extraProperties = {} } = options;

  Sentry.withScope(scope => {
    const capabilityTags = capabilitiesToTags(capabilities);

    const otherTags: SentryTags = {
      ...tags,
      // These errors always affect rendering. Meaning that, if left unhandled, they would
      // crash the app. We always want to include this tag to designate them as such.
      errorKind: 'rendering',
    };

    scope.setTags({
      ...otherTags,
      ...capabilityTags,
    });

    scope.setExtras(extraProperties);

    Sentry.captureReactException(ex, errorInfo);
  });
}

/**
 * @deprecated
 *
 * Please use `captureTaggedReactException` for any rendering errors, or
 * `captureTaggedException` for non-rendering (event/handler/async) errors instead.
 * */
export function captureException(ex: Error, payload: SentryPayload = {}, tags?: SentryTags) {
  if (payload) {
    Sentry.withScope(scope => {
      Object.entries(payload).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });

      if (tags) {
        Sentry.setTags(tags);
      }

      Sentry.captureException(ex);
    });
  } else {
    Sentry.captureException(ex);
  }
}

export const captureRecaptchaException = (
  ex: Error,
  payload: SentryPayload = {},
  tag: RecaptchaTag
) => {
  captureException(ex, payload, {
    recaptcha: tag,
  });
};

export const captureCobrandingException = (
  ex: Error,
  payload: SentryPayload = {},
  tag: CobrandingTag
) => {
  captureException(ex, payload, {
    coBranding: tag,
  });
};

export const captureMalformedError = (
  ex: Error,
  payload: SentryPayload = {},
  tag: MalformedErrorTag
) => {
  captureException(ex, payload, { malformedError: tag });
};

export const captureCobrandingError = (
  msg: string,
  payload: SentryPayload = {},
  tag: CobrandingTag
) => {
  captureMessage(msg, payload, 'error', {
    coBranding: tag,
  });
};

/**
 * @deprecated
 *
 * Please use `captureTaggedReactException` for any rendering errors, or
 * `captureTaggedException` for non-rendering (event/handler/async) errors instead.
 * */
export function captureErrorMessage(msg: string, payload: SentryPayload = {}) {
  captureMessage(msg, payload, 'error');
}

export const captureRecaptchaMessage = (
  msg: string,
  payload: SentryPayload = {},
  tag: RecaptchaTag
) => {
  captureMessage(msg, payload, 'error', {
    recaptcha: tag,
  });
};

/**
 * Prefer `captureTaggedReactException` or `captureTaggedException` when handling unexpected errors.
 *
 * These helpers will capture the whole error object including a stack trace if there is one, which makes
 * the resulting issues on Sentry a lot more readable and actionable.
 *
 * Capturing a message only is fine for handling highly specific, expected errors at a fine-grained
 * level.
 * */
export function captureMessage(
  msg: string,
  payload: SentryPayload,
  severity: Sentry.SeverityLevel,
  tags?: SentryTags
) {
  if (payload) {
    Sentry.withScope(scope => {
      Object.entries(payload).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });

      if (tags) {
        Sentry.setTags(tags);
      }

      Sentry.captureMessage(msg, severity);
    });
  } else {
    Sentry.captureMessage(msg, severity);
  }
}

/**
 * Sentry only allows primitives for tag values.
 *
 * Since an error may be related to more than one capability group/subgroup/name, we need the
 * option to assign multiple different capability tags.
 *
 * For example, an error during check-username could impact both password+username login as well
 * as SSO login - both cases will need to be represented with their own tag.
 *
 * i.e. we can't include an array of affected capabilities under a single tag, so we include
 * them all individually as both the key and the value, like:
 *
 * { 'capability-group:login': 'capability-group:login' };
 */
const capabilitiesToTags = (capabilities: Capability[]) =>
  capabilities.reduce((acc, current) => {
    return {
      ...acc,
      [current]: current,
    };
  }, {} as Record<Capability, Capability>);
